import { useState, useRef, type ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CalendarBlankIcon } from "@phosphor-icons/react/CalendarBlank";
import { XIcon } from "@phosphor-icons/react/X";
import { Icon } from "../../Icons/Icon";
import { PortalContextReset } from "../../Controls/Internal/PortalContextReset";
import { useMarkDialogDirty } from "../../Dialog/FormDirtyContext";
import { useIsFormItemError } from "../../FormLayout/FormItemErrorProvider";
import { useControlBarSlot } from "../../PageLayout/Internal/ControlBarContext";
import { useControlPresentation } from "../../Controls/Internal/useControlPresentation";
import {
  dateTriggerText,
  dateTriggerUnderline,
} from "../Internal/dateFieldStyles";
import { formatLocalDate } from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";
import { InlineCalendar } from "./InlineCalendar";
import { usePopoverZIndex } from "../../Theme/overlayZIndex";

interface DatePickerProps {
  value?: LocalDate | undefined;
  onChange?: ((date: LocalDate | undefined) => void) | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  allowClear?: boolean | undefined;
  disabledDate?: ((date: LocalDate) => boolean) | undefined;
  /** Grey out and prevent selection of dates before today. Composes with disabledDate. */
  disablePastDates?: boolean | undefined;
  /** Short reason shown in a tooltip when hovering a disabled day, explaining why it can't be picked. */
  disabledHint?: ReactNode | undefined;
  /** Force the red invalid border (e.g. a pre-populated past date) without waiting for the FormItem touched/submitted gate. */
  invalid?: boolean | undefined;
  /**
   * Swap the "November 2025" caption for month and year dropdowns. Turn it on
   * for dates that sit years from today — a join date, a birthday — where
   * arrowing month by month is unusable.
   */
  yearNavigation?: boolean | undefined;
  /** First year the dropdown offers. Only used with `yearNavigation`. */
  startYear?: number | undefined;
  /** Last year the dropdown offers. Only used with `yearNavigation`. */
  endYear?: number | undefined;
  /** Lands on the trigger, the element a test clicks to open the calendar. */
  "data-testid"?: string | undefined;
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  disabled = false,
  allowClear = false,
  disabledDate,
  disablePastDates = false,
  disabledHint,
  invalid = false,
  yearNavigation = false,
  startYear,
  endYear,
  "data-testid": dataTestid,
}: DatePickerProps) {
  const popoverZ = usePopoverZIndex();
  const markDirty = useMarkDialogDirty();
  const formItemError = useIsFormItemError();
  const isError = invalid || formItemError;
  // Boxed by default; the spacious customer flows opt into the underline look
  // via the control presentation. Inside a filter/control bar the box tightens
  // (h-8 + less padding).
  const inControlBar = useControlBarSlot() !== false;
  const boxedCompact = inControlBar;
  const underlined = useControlPresentation() === "underlined";
  const [open, setOpen] = useState(false);
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const anchorRef = useRef<HTMLDivElement>(null);

  function handleClear(e: React.MouseEvent): void {
    e.stopPropagation();
    markDirty?.();
    onChange?.(undefined);
  }

  const displayText = value ? formatLocalDate(value) : undefined;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <div
          ref={anchorRef}
          role="button"
          tabIndex={disabled ? -1 : 0}
          {...(dataTestid !== undefined && { "data-testid": dataTestid })}
          onClick={() => !disabled && !open && setOpen(true)}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
          className={
            underlined
              ? dateTriggerUnderline({
                  isError,
                  open,
                  disabled,
                  compact: boxedCompact,
                })
              : `group inline-flex ${boxedCompact ? "h-[26px]" : "h-9"} cursor-pointer items-center gap-2 rounded-md border ${isError ? "border-red" : open ? "border-brand" : "border-border-strong hover:border-brand"} bg-card ${boxedCompact ? "px-1.5" : "px-3"} transition-colors ${disabled ? "pointer-events-none opacity-40" : ""}`
          }
        >
          <Icon
            icon={CalendarBlankIcon}
            size="sm"
            color="text-muted-foreground"
          />
          <span
            className={
              underlined
                ? dateTriggerText(!!displayText, boxedCompact)
                : `${boxedCompact ? "text-xs" : "text-sm"} font-medium ${displayText ? "text-foreground" : "text-faint"}`
            }
          >
            {displayText ?? placeholder}
          </span>
          {allowClear && value && !disabled && (
            <button
              type="button"
              aria-label="Clear date"
              tabIndex={-1}
              onClick={handleClear}
              onMouseDown={(e) => e.preventDefault()}
              className="shrink-0 text-content-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-content-muted"
            >
              <Icon icon={XIcon} size="xs" />
            </button>
          )}
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          style={{ zIndex: popoverZ }}
          align="start"
          sideOffset={4}
          className="rounded-xl border border-border bg-card shadow-floating"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (
              e.target instanceof Node &&
              anchorRef.current?.contains(e.target)
            ) {
              e.preventDefault();
            }
          }}
        >
          {/* Portaled content must not inherit the trigger surface's layout
              contexts (table, control bar, …) — see PortalContextReset. */}
          <PortalContextReset>
            <InlineCalendar
              value={value}
              onChange={(date) => {
                onChange?.(date);
                setOpen(false);
              }}
              disablePastDates={disablePastDates}
              disabledDate={disabledDate}
              disabledHint={disabledHint}
              yearNavigation={yearNavigation}
              startYear={startYear}
              endYear={endYear}
            />
          </PortalContextReset>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
